import { useReducer, useState, useEffect, useRef, useMemo } from "react";
const SAVED_FEEDBACK_TIME_MS = 2500;
const REQUIRED_FN = (val) => {
    return (Array.isArray(val) && !val.length) || // if the control is a multi-pick SelectListField vall is an array
        val === undefined ||
        val === null ||
        (typeof val === "string" && val.trim() === "")
        ? "Required"
        : "";
};
export const useFormState = (rawModel, rawRules, onSubmit, isFormDisabled, arrayKeys = []) => {
    // make out convenience "required" entries into normal ["name", fn] entries
    const validationRules = rawRules.map(rule => typeof rule === "string" ? [rule, ({ val }) => REQUIRED_FN(val)] : rule);
    const { flattenedModel, arrayMeta, fieldMeta } = useMemo(() => processIncomingModel(rawModel, arrayKeys), [rawModel, arrayKeys]);
    const initialState = {
        values: Object.assign({}, flattenedModel),
        initialValues: Object.assign({}, flattenedModel),
        fieldMeta,
        arrayMeta,
        hasError: false,
        isTouched: false,
    };
    // track our state values and meta using a reducer
    const reducer = (state, action) => {
        const { values, fieldMeta: meta, initialValues } = state;
        const { type } = action;
        if (type === "rerender") {
            return Object.assign({}, state);
        }
        if (type === "reset") {
            return initialState;
        }
        // name should always be here now
        const name = action.name;
        if (type === "addArrayItem") {
            const index = state.arrayMeta[name].length;
            return Object.assign(Object.assign({}, state), { arrayMeta: Object.assign(Object.assign({}, state.arrayMeta), { [name]: [
                        ...state.arrayMeta[name],
                        {
                            isNew: true,
                            isDeleted: false,
                            propName: fieldName => `${name}/${index}/${fieldName}`,
                            index,
                        },
                    ] }), isTouched: true });
        }
        if (type === "deleteArrayItem") {
            let newFieldMeta = Object.assign({}, state.fieldMeta);
            Object.keys(state.fieldMeta)
                .filter(key => key.startsWith(`${name}/${action.value}/`))
                .forEach(key => {
                newFieldMeta = Object.assign(Object.assign({}, newFieldMeta), { [key]: Object.assign(Object.assign({}, newFieldMeta[key]), { error: undefined }) });
            });
            const hasError = Object.values(newFieldMeta).some(meta => meta.error);
            return Object.assign(Object.assign({}, state), { arrayMeta: Object.assign(Object.assign({}, state.arrayMeta), { [name]: [
                        ...state.arrayMeta[name].slice(0, action.value),
                        Object.assign(Object.assign({}, state.arrayMeta[name][action.value]), { isDeleted: true }),
                        ...state.arrayMeta[name].slice(action.value + 1),
                    ] }), fieldMeta: newFieldMeta, hasError, isTouched: true });
        }
        if (type === "change") {
            const error = asessError(state, validationRules, name, action.value);
            let newFieldMeta = Object.assign(Object.assign({}, meta), { [name]: {
                    touched: true,
                    isChanged: action.value !== initialValues[name],
                    error,
                } });
            const hasError = error || Object.values(newFieldMeta).some(meta => meta.error);
            return Object.assign(Object.assign({}, state), { values: Object.assign(Object.assign({}, values), { [name]: action.value }), fieldMeta: newFieldMeta, hasError, isTouched: true });
        }
        if (type === "touch") {
            const error = asessError(state, validationRules, name, state.values[name]);
            let newFieldMeta = Object.assign(Object.assign({}, meta), { [name]: {
                    touched: true,
                    isChanged: state.values[name] !== initialValues[name],
                    error,
                } });
            const hasError = error || Object.values(newFieldMeta).some(meta => meta.error);
            return Object.assign(Object.assign({}, state), { values: Object.assign(Object.assign({}, values), { [name]: state.values[name] }), fieldMeta: newFieldMeta, hasError });
        }
        return Object.assign({}, state);
    };
    const [state, localDispatch] = useReducer(reducer, initialState);
    const [trySubmitting, setTrySubmitting] = useState(false);
    // use a ref rather than state because refs do not trigger rerenders, and this helped remove an
    //  inifinite loop of rerenders
    const lifecycleRef = useRef("normal");
    const handleOnChange = ({ name, value }) => {
        localDispatch({ type: "change", name, value });
    };
    const handleOnBlur = (name) => {
        localDispatch({ type: "touch", name });
    };
    const triggerReset = () => {
        localDispatch({ type: "reset" });
    };
    const triggerRerender = () => {
        localDispatch({ type: "rerender" });
    };
    const triggerSubmit = () => {
        // when submitting we ensure all validatable fields are touched and have had a chance to validate
        const fieldNameSplits = validationRules.map(rule => rule[0].split("."));
        const normalFieldsToTouch = fieldNameSplits
            .filter(splits => splits.length === 1)
            .map(splits => splits[0]);
        const arrayFieldsToTouch = fieldNameSplits
            .filter(splits => splits.length === 2)
            .map(splits => {
            const arrayName = splits[0];
            const fieldName = splits[1];
            return state.arrayMeta[arrayName].map(({ index }) => `${arrayName}/${index}/${fieldName}`);
        })
            .reduce((acc, curr) => acc.concat(...curr), []);
        [...normalFieldsToTouch, ...arrayFieldsToTouch].forEach(fieldName => localDispatch({ type: "touch", name: fieldName }));
        lifecycleRef.current = "submitting";
        setTrySubmitting(true);
    };
    const addArrayItem = (arrayName) => {
        localDispatch({ type: "addArrayItem", name: arrayName });
    };
    const deleteArrayItem = (arrayName, index) => {
        localDispatch({ type: "deleteArrayItem", name: arrayName, value: index });
    };
    // a function to cut down on bolierplate passing props to inputs
    //  used e.g. <TextField {...formProps("my-field-name")}
    const formProps = (name, passNullOrUndefined) => {
        var _a, _b;
        const value = passNullOrUndefined && (state.values[name] === null || state.values[name] === undefined)
            ? ""
            : state.values[name];
        return {
            name,
            value,
            disabled: isFormDisabled || lifecycleRef.current === "submitting",
            error: !!((_a = state.fieldMeta[name]) === null || _a === void 0 ? void 0 : _a.error),
            helperText: (_b = state.fieldMeta[name]) === null || _b === void 0 ? void 0 : _b.error,
            onBlur: () => handleOnBlur(name),
            // all controls we use at the moment have an onChange function that passes e.currentTarget
            //  - TextField does it out of the box
            //  - SelectListField and SearchListField are our customer controls and so we shape onChange to match TextField
            //  - but KeyboardDatePicker does not pass e.currentTarget but instead passes a Date value as its argument to onChange
            //    so we accomodate dates here also:
            onChange: (target) => {
                var _a;
                const value = target instanceof Date
                    ? target
                    : ((_a = target === null || target === void 0 ? void 0 : target.currentTarget) === null || _a === void 0 ? void 0 : _a.value) || (target === null || target === void 0 ? void 0 : target.target.value);
                handleOnChange({
                    name,
                    value,
                });
            },
        };
    };
    // file inputs are a special case as they want to be feeding back a handle to the file to the form state
    const formPropsFileInput = (name) => {
        const formPropsResult = formProps(name);
        return Object.assign(Object.assign({}, formPropsResult), { onChange: (target) => {
                var _a, _b;
                handleOnChange({ name: "file", value: (_b = (_a = target === null || target === void 0 ? void 0 : target.currentTarget) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b[0] });
            }, value: "" });
    };
    // file inputs are a special case as they want to be feeding back a handle to the file to the form state
    const formPropsMultipleFileInput = (name) => {
        const formPropsResult = formProps(name);
        return Object.assign(Object.assign({}, formPropsResult), { onChange: (target) => {
                const nextValue = target.target.value || [];
                const filteredItems = nextValue.filter((v, i, a) => a.findIndex((t) => t.name === v.name) === i);
                handleOnChange({ name: "file", value: filteredItems ? filteredItems : [] });
            } });
    };
    const formPropsCheckbox = (name) => {
        return {
            name,
            checked: !!state.values[name],
            value: state.values[name],
            onChange: (target) => {
                handleOnChange({
                    name,
                    value: !!target.currentTarget.checked,
                });
            },
        };
    };
    useEffect(() => {
        // need to handle the submit inside an effect because the validation action/reducing process kicked off in
        //  handleSubmit is not synchronous, so we need to let the task queue flush and then check for a remaining error(s)
        if (trySubmitting) {
            if (state.hasError) {
                lifecycleRef.current = "normal";
            }
            else {
                onSubmit({
                    state,
                    patch: modelToJsonPatchDocument(state),
                    model: unflattenModel(state),
                    initialModel: rawModel,
                });
            }
            setTrySubmitting(false);
        }
    }, [state, trySubmitting, onSubmit, arrayKeys, rawModel]);
    useEffect(() => {
        // this effect only gets hit when the supplied initialModel changes (see deps array), which represents
        //  the reload of the model from the backing store, which lets us know a save has occurred.
        if (lifecycleRef.current === "submitting") {
            // if we just update the ref then we have no trigger to feeback the change to the outside world,
            //  so trigger a rerender also.
            lifecycleRef.current = "saved";
            triggerReset();
            const timeout = setTimeout(() => {
                lifecycleRef.current = "normal";
                triggerRerender();
            }, SAVED_FEEDBACK_TIME_MS);
            return () => clearTimeout(timeout);
        }
    }, [flattenedModel, lifecycleRef]);
    return {
        formProps,
        formPropsMultipleFileInput,
        formPropsFileInput,
        formPropsCheckbox,
        state,
        handleOnChange,
        handleOnBlur,
        triggerSubmit,
        triggerReset,
        lifecycleStage: lifecycleRef.current,
        addArrayItem,
        deleteArrayItem,
    };
};
const processIncomingModel = (model, arrayKeys) => {
    let arrayMeta = {};
    let fieldMeta = {};
    let flattenedModel = Object.assign({}, model);
    for (const arrayKey of arrayKeys) {
        const thisArray = flattenedModel[arrayKey];
        arrayMeta[arrayKey] = [];
        thisArray.forEach((item, index) => {
            Object.keys(item).forEach(key => {
                flattenedModel[`${arrayKey}/${index}/${key}`] = item[key];
            });
            arrayMeta[arrayKey].push({
                propName: name => `${arrayKey}/${index}/${name}`,
                isNew: false,
                isDeleted: false,
                index,
            });
        });
    }
    return {
        flattenedModel,
        arrayMeta,
        fieldMeta,
    };
};
const unflattenModel = (state) => {
    const { values, arrayMeta } = state;
    let outModel = Object.assign({}, values);
    const keys = Object.keys(outModel);
    for (const arrayName in arrayMeta) {
        const array = arrayMeta[arrayName].map(({ index }) => {
            const myKeyRoot = `${arrayName}/${index}/`;
            const myKeys = keys.filter(key => key.startsWith(myKeyRoot));
            const myObject = myKeys.reduce((acc, curr) => {
                acc[curr.replace(myKeyRoot, "")] = values[curr];
                return acc;
            }, {});
            return myObject;
        });
        outModel[arrayName] = array;
    }
    return outModel;
};
const asessError = (state, rules, fieldName, value) => {
    const fieldNameSplits = fieldName.split("/");
    const isAnArrayField = fieldNameSplits.length > 1;
    const arrayParentName = isAnArrayField ? fieldNameSplits[0] : "";
    const arrayIndex = isAnArrayField ? +fieldNameSplits[1] : undefined;
    fieldName = isAnArrayField ? fieldNameSplits[2] : fieldName;
    // special case: if a deleted array field, do not validate!
    const isADeletedArrayField = isAnArrayField && state.arrayMeta[arrayParentName][arrayIndex].isDeleted;
    if (isADeletedArrayField) {
        return "";
    }
    const thisFieldValidationFunctions = rules
        .filter(ruleTuple => {
        const ruleFieldName = ruleTuple[0];
        return isAnArrayField
            ? ruleFieldName === `${arrayParentName}.${fieldName}` // array field
            : ruleFieldName === fieldName; // normal field
    })
        .map(ruleTuple => ruleTuple[1]);
    const validationFailures = thisFieldValidationFunctions
        .map(validateFn => validateFn({ val: value, state, requiredFn: REQUIRED_FN, arrayIndex }))
        // following filter will only return rules that have generated a non-empty message...
        .filter(result => !!result);
    //  ... so the following is safe, just need to keep typescript happy
    return validationFailures[0];
};
const modelToJsonPatchDocument = (state) => {
    const { values, fieldMeta, arrayMeta } = state;
    let changedKeys = Object.keys(fieldMeta).filter(key => fieldMeta[key].isChanged);
    let deleteOperations = [];
    let addOperations = [];
    for (const arrayName in arrayMeta) {
        const thisArrayMeta = arrayMeta[arrayName];
        // special case: disregard array items that have been added and deleted in the same session
        for (const entity of thisArrayMeta.filter(item => item.isNew && item.isDeleted)) {
            changedKeys = changedKeys.filter(fieldKey => !fieldKey.startsWith(`${arrayName}/${entity.index}/`));
        }
        for (const entity of thisArrayMeta.filter(item => !item.isNew && item.isDeleted)) {
            deleteOperations.push({ op: "remove", path: `/${arrayName}/${entity.index}` });
            changedKeys = changedKeys.filter(fieldKey => !fieldKey.startsWith(`${arrayName}/${entity.index}/`));
        }
        for (const entity of thisArrayMeta.filter(item => item.isNew && !item.isDeleted)) {
            const newModelKeys = changedKeys.filter(fieldKey => fieldKey.startsWith(`${arrayName}/${entity.index}/`));
            const newModel = newModelKeys.reduce((acc, fieldKey) => {
                const nonPrefixedKey = fieldKey.replace(`${arrayName}/${entity.index}/`, "");
                acc[nonPrefixedKey] = values[fieldKey];
                return acc;
            }, {});
            addOperations.push({ op: "add", path: `/${arrayName}/-`, value: newModel });
            changedKeys = changedKeys.filter(key => !key.startsWith(`${arrayName}/${entity.index}/`));
        }
    }
    // We need to make sure that multiple deletes arrive in reverse index order eg.
    //  [{... path: "/foo/2"}, {... path: "/foo/1"}]
    //  and not
    //  [{... path: "/foo/1"}, {... path: "/foo/2"}].
    //  I think dotnet applies the deletes in order and mutates in place, so would delete item 1 in the array
    //  (removing the item from the array and shortening it by one) AND THEN try to delete item 2 which would now not be there.
    // If you make it delete from the end backwards, the target items will always be there.
    deleteOperations = deleteOperations.sort((a, b) => (a.path > b.path ? -1 : 1));
    const updateOperations = changedKeys.map(key => ({
        op: "replace",
        path: `/${key}`,
        value: values[key],
    }));
    return [
        ...updateOperations,
        // the deletes have to go after the updates. It looks like dotnet applies the operations in the order it finds them in
        //  this array and because it deals with items in child arrays by numerical index, if it has deleted any items before applying updates,
        //  it falls over!
        ...deleteOperations,
        ...addOperations,
    ];
};
